/**
 * D12 · pre-flight as a work queue, not a report.
 *
 *     Not yet · 15 blocking · 4 advisory
 *     [Process structure 2] [Repetition 1] [Monitoring 1] [Signatures 8] …
 *     …the findings themselves
 *
 * The dimension chips come from the checks' own `dimension`, so the grouping cannot drift
 * from what it is grouping. Counts are **instances**, not findings: one check firing on eight
 * signatures is eight things to fix, and a queue that said "1" would be counting the wrong
 * noun.
 *
 * A queue is a list of work. It is ordered blocking-first for that reason — what stops the
 * record being issued is what to do next, and sorting by dimension or id would bury it.
 */
import React, { useMemo, useState } from "react";
import { Empty, Flex, Typography } from "antd";

import Finding, { findingCount } from "./Finding";

const { Text, Title } = Typography;

const instances = findingCount;

export default function PreflightQueue({ findings = [], onGoTo, onFix, title = "Pre-flight" }) {
  const [dim, setDim] = useState(null);

  const counts = useMemo(() => {
    const out = { blocking: 0, advisory: 0, byDim: new Map() };
    findings.forEach((f) => {
      const n = instances(f);
      out[f.severity === "blocking" ? "blocking" : "advisory"] += n;
      const key = f.dimension || "Other";
      out.byDim.set(key, (out.byDim.get(key) || 0) + n);
    });
    return out;
  }, [findings]);

  const shown = useMemo(() => {
    const rows = dim ? findings.filter((f) => (f.dimension || "Other") === dim) : findings;
    // Blocking first: what stops the record being issued is what to do next.
    return [...rows].sort((a, b) => {
      if (a.severity !== b.severity) return a.severity === "blocking" ? -1 : 1;
      return instances(b) - instances(a);
    });
  }, [findings, dim]);

  return (
    <Flex vertical gap={12}>
      <Flex vertical gap={2}>
        <Title level={5} style={{ margin: 0 }}>{title}</Title>
        <Flex gap={8} align="baseline">
          {counts.blocking ? (
            <>
              <Text strong style={{ color: "var(--dz-block)" }}>Not yet</Text>
              <Text type="secondary">·</Text>
            </>
          ) : (
            <Text strong style={{ color: "var(--dz-ok)" }}>Ready</Text>
          )}
          <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
            {counts.blocking} blocking · {counts.advisory} advisory
          </Text>
        </Flex>
      </Flex>

      {counts.byDim.size ? (
        <Flex gap={6} wrap>
          {[...counts.byDim.entries()].sort((a, b) => b[1] - a[1]).map(([name, n]) => (
            <button
              key={name}
              className={`u0-dimchip ${dim === name ? "on" : ""}`}
              onClick={() => setDim(dim === name ? null : name)}
            >
              {name}<span className="n">{n}</span>
            </button>
          ))}
        </Flex>
      ) : null}

      {shown.length ? (
        <Flex vertical gap={8}>
          {shown.map((f) => (
            <Finding key={f.id} finding={f} placement="queue" onGoTo={onGoTo} onFix={onFix} />
          ))}
        </Flex>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={dim ? "Nothing in this dimension." : "Nothing is blocking this record."}
        />
      )}
    </Flex>
  );
}
